<template>
    <Overlay :show="loading">
        <div v-if="previewUrl" class="review-preview-wrap" :style="previewWrapStyle">
            <iframe :src="previewUrl" class="review-preview-frame" title="review-document"/>
        </div>

        <b-card v-else :title="$t('Component(ReviewProcess):source-document')" class="mb-1">
            <div>
                <div class="mb-1"><strong>{{ $t('Component(ReviewProcess):file-reference') }}:</strong> <span>{{ fileReferenceId || '-' }}</span></div>
                <div class="mb-1">
                    <b-button size="sm" variant="outline-primary" :disabled="!downloadUrl" :href="downloadUrl" target="_blank">
                        {{ $t('Component(Button):open-document') }}
                    </b-button>
                </div>
                <div class="text-muted small">
                    {{ $t('Component(ReviewProcess):pdf-preview-unavailable') }}
                </div>
            </div>
        </b-card>
    </Overlay>
</template>

<script>
import {BCard, BButton} from 'bootstrap-vue'
import {ResearchReviewApi} from '@core/api'
import Overlay from '@core/components/common/Overlay.vue'

export default {
    name: 'DocumentPreviewSection',
    components: {
        BCard,
        BButton,
        Overlay,
    },
    props: {
        fileReferenceId: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            downloadUrl: '',
            previewUrl: '',
            loading: false,
            viewportHeight: 0,
        }
    },
    computed: {
        previewWrapStyle() {
            const baseHeight = this.viewportHeight > 0
                ? this.viewportHeight - 170
                : 800

            const height = Math.max(520, baseHeight)
            return {
                height: height + 'px',
            }
        },
    },
    watch: {
        fileReferenceId: {
            immediate: true,
            handler() {
                this.bootstrap()
            },
        },
    },
    methods: {
        bootstrap() {
            this.loading = true
            this.downloadUrl = ''
            this.cleanupPreviewUrl()
            Promise.resolve(this.resolveDownloadUrl())
                .finally(() => {
                    this.loading = false
                })
        },
        resolveDownloadUrl() {
            if (!this.fileReferenceId) {
                return
            }

            return ResearchReviewApi
                .resolveFileDownloadUrl(this.fileReferenceId)
                .then((response) => {
                    this.downloadUrl = response.data && response.data.downloadUrl ? response.data.downloadUrl : ''
                    return this.preparePdfPreview()
                })
                .catch(() => {
                    this.downloadUrl = ''
                    this.cleanupPreviewUrl()
                })
        },
        preparePdfPreview() {
            if (!this.downloadUrl) {
                return
            }

            return ResearchReviewApi
                .downloadFileByUrl(this.downloadUrl)
                .then((response) => {
                    const contentType = response && response.headers
                        ? (response.headers['content-type'] || '')
                        : ''

                    if (!contentType.includes('application/pdf')) {
                        this.cleanupPreviewUrl()
                        return
                    }

                    const pdfBlob = new Blob([response.data], {type: 'application/pdf'})
                    this.previewUrl = URL.createObjectURL(pdfBlob)
                })
                .catch(() => {
                    this.cleanupPreviewUrl()
                })
        },
        cleanupPreviewUrl() {
            if (this.previewUrl) {
                URL.revokeObjectURL(this.previewUrl)
                this.previewUrl = ''
            }
        },
        onResize() {
            this.viewportHeight = window.innerHeight || 0
        },
    },
    mounted() {
        this.onResize()
        window.addEventListener('resize', this.onResize, {passive: true})
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.onResize)
        this.cleanupPreviewUrl()
    },
}
</script>

<style scoped>
.review-preview-wrap {
    border: 1px solid #e5e5e5;
    border-radius: 0.25rem;
    overflow: hidden;
    background: #f8f8f8;
}

.review-preview-frame {
    width: 100%;
    height: 100%;
    border: 0;
}
</style>
